<template>
  <div v-if="event.deletedAt == null">
    <LISAMenu
      v-model="menuVisible"
      class="EventButtonList"
      :items="menuItems"
      placement="right-start"
      :disabled="loadingMenu"
    >
      <template #trigger="{ triggerAttrs }">
        <LISAUIButton
          v-bind="triggerAttrs"
          :style="toggleButtonStyle"
          icon="far fa-plus-square"
          size="sm"
          text
          :disabled="loadingMenu"
          @click.stop
        />
      </template>
    </LISAMenu>
    <SQDCEventActionModalComponent
      v-if="selectedSource != null"
      :source="selectedSource"
      :site-id="props.siteId"
      :board-id="boardIdForCreation"
      @close="hideModal"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, type PropType, ref } from "vue";
import {
  LISAButton as LISAUIButton,
  LISAMenu,
  type LISAMenuGroup,
  type LISAMenuItem,
} from "@fi-solutions/lisa-ui";
import { useTranslation } from "@/language/translation";
import {
  SQDCEventActionTypeInternalType,
  SQDCEventActionTypeType,
  useSQDCActionTypesQuery,
  type SQDCEABShorthandsQuery,
  useSQDCEABShorthandsQuery,
} from "@/graphql";
import type { EABEventType } from "../EventActionHelper";
import type { ModalActionEditType, ModalEventType } from "../modals/SQDCModelHelper";
import {
  createDefaultActionForWorkflow,
  createDefaultActionFromShorthand,
  getModalEventTypeForLatestRevision,
} from "../modals/SQDCShorthandDefaultHelper";
import { useSQDCWorkflowDefinitions } from "../../../workflow/useSQDCWorkflows";
import {
  getSQDCWorkflowStartNodes,
  getSQDCWorkflowStartTransitions,
  resolveSQDCWorkflowStartConnectionLabel,
  type SQDCWorkflowDefinition,
} from "../../../workflow/domain";
import SQDCEventActionModalComponent from "../SQDCEventActionModalComponent.vue";
import { PSC_EVENT_INTERNAL_TYPES } from "@/modules/psc/PSCEventTypes";
import { useSQDCDisplayName } from "../../../helper/SQDCDisplayNameHelper";

type SQDCShorthand = SQDCEABShorthandsQuery["SQDCEABShorthands"][number];
type ActionModalSource = {
  kind: "action-create";
  actionType: ModalEventType;
  eventId: number;
  eventOwnerId?: number;
  defaultAction?: ModalActionEditType;
  userOptionIds?: number[];
  workflow?: {
    definitionId?: string;
    startNodeId?: string;
    workflowInstanceId?: string;
    workflowStepId?: string;
    connectionId?: string;
  };
  workflowDefinition?: SQDCWorkflowDefinition;
  workflowNode?: SQDCWorkflowDefinition["nodes"][number];
  responsibleShorthand?: SQDCShorthand;
};
type EventConversionModalSource = {
  kind: "event-edit";
  eventId: number;
  eventType: ModalEventType;
  initialConversionTargetId: number;
};

const props = defineProps({
  event: {
    type: Object as PropType<EABEventType>,
    required: true,
  },
  siteId: {
    type: Number,
    required: true,
  },
  boardId: {
    type: Number,
    required: false,
    default: undefined,
  },
});

const { t, locale } = useTranslation();
const displayName = useSQDCDisplayName();

const boardIdForCreation = computed<number | undefined>(
  () =>
    props.boardId ??
    props.event.originalBoardId ??
    props.event.BoardEventConnection.toSorted((a, b) => a.createdAt.localeCompare(b.createdAt))[0]
      ?.boardId,
);

const { result: actionTypeResult, loading: loadingActionTypes } = useSQDCActionTypesQuery(
  computed(() => ({
    boardIds: boardIdForCreation.value == null ? undefined : [boardIdForCreation.value],
    types: SQDCEventActionTypeType.ACTION,
  })),
  {
    fetchPolicy: "cache-first",
  },
);

const actionTypes = computed(() =>
  boardIdForCreation.value == null ? [] : (actionTypeResult.value?.SQDCEventActionTypes ?? []),
);

const { result: siteEventTypeResult, loading: loadingSiteEventTypes } = useSQDCActionTypesQuery(
  {
    siteIds: props.siteId,
    types: SQDCEventActionTypeType.EVENT,
  },
  { fetchPolicy: "cache-first" },
);
const siteEventTypes = computed(() =>
  (siteEventTypeResult.value?.SQDCEventActionTypes ?? []).map((type) =>
    getModalEventTypeForLatestRevision(type as ModalEventType),
  ),
);
const sourceEventType = computed(() =>
  siteEventTypes.value.find(({ id }) => id === props.event.typeId),
);
const conversionTargets = computed(() => {
  const pscTypes = new Set<SQDCEventActionTypeInternalType>(PSC_EVENT_INTERNAL_TYPES);
  if (
    sourceEventType.value?.internalType == null ||
    pscTypes.has(sourceEventType.value.internalType)
  ) {
    return [];
  }
  return siteEventTypes.value.filter(
    (type) => type.internalType != null && pscTypes.has(type.internalType),
  );
});

const { result: shorthandResult, loading: loadingShorthands } = useSQDCEABShorthandsQuery(
  computed(() => ({
    boardIds: boardIdForCreation.value ?? undefined,
  })),
  {
    fetchPolicy: "cache-first",
  },
);

const eabShorthands = computed(() => {
  return (
    shorthandResult.value?.SQDCEABShorthands.filter(
      (shorthand) =>
        shorthand.EventActionType?.type === SQDCEventActionTypeType.ACTION &&
        isShorthandAvailableForBoard(shorthand),
    ) ?? []
  );
});

const selectedSource = ref<ActionModalSource | EventConversionModalSource>();
const menuVisible = ref(false);

const toggleButtonStyle = computed(() => ({
  border: 0,
  ...(menuVisible.value
    ? {
        background: "var(--p-primary-color)",
        color: "white",
      }
    : {}),
}));

const actionMenuItems = computed<LISAMenuItem[]>(() =>
  actionTypes.value.map((actionType) => ({
    id: `action-${actionType.id}`,
    label: displayName(actionType),
    icon: `${actionType.icon ?? "far fa-comment"} text-primary`,
    callback: () => openActionModal(actionType),
  })),
);

const shorthandButtonList = computed<LISAMenuItem[]>(() =>
  eabShorthands.value.map((shorthand) => ({
    id: `shorthand-${shorthand.id}`,
    label: displayName(shorthand),
    icon: `${shorthand.icon ?? shorthand.EventActionType?.icon ?? "far fa-comment-dots"} text-primary`,
    callback: () => openSQDCShorthandModal(shorthand),
  })),
);

const { definitions: workflowDefinitions, loading: loadingWorkflows } = useSQDCWorkflowDefinitions(
  computed(() => ({
    boardIds: boardIdForCreation.value == null ? undefined : [boardIdForCreation.value],
  })),
);
const workflowShorthandIds = computed(() =>
  workflowDefinitions.value
    .flatMap((definition) => getSQDCWorkflowStartNodes(definition))
    .flatMap((node) => (node.shorthandId == null ? [] : [node.shorthandId])),
);
const { result: workflowShorthandResult, loading: loadingWorkflowShorthands } =
  useSQDCEABShorthandsQuery(
    computed(() => ({ ids: workflowShorthandIds.value })),
    { fetchPolicy: "cache-first" },
  );
const workflowShorthands = computed(() => workflowShorthandResult.value?.SQDCEABShorthands ?? []);
const loadingMenu = computed(
  () =>
    loadingActionTypes.value ||
    loadingSiteEventTypes.value ||
    loadingShorthands.value ||
    loadingWorkflows.value ||
    loadingWorkflowShorthands.value,
);
const workflowButtonList = computed<LISAMenuItem[]>(() =>
  boardIdForCreation.value == null
    ? []
    : workflowDefinitions.value.flatMap((definition) =>
        getSQDCWorkflowStartTransitions(definition)
          .filter(({ firstNode }) => firstNode.type === "ACTION")
          .map(({ startNode, connection, firstNode }) => ({
            id: `workflow-${definition.id}-${connection.id}`,
            label: resolveSQDCWorkflowStartConnectionLabel(connection, locale.value, t),
            icon: `${definition.icon ?? "fas fa-diagram-project"} text-primary`,
            callback: () => openWorkflow(definition, startNode, firstNode),
          })),
      ),
);
const conversionButtonList = computed<LISAMenuItem[]>(() =>
  conversionTargets.value.map((targetType) => ({
    id: `conversion-${targetType.id}`,
    label: displayName(targetType),
    icon: "fas fa-arrow-right-arrow-left text-primary",
    callback: () => openConversionModal(targetType),
  })),
);

function openConversionModal(targetType: ModalEventType) {
  if (sourceEventType.value == null) return;
  selectedSource.value = {
    kind: "event-edit",
    eventId: props.event.id,
    eventType: sourceEventType.value,
    initialConversionTargetId: targetType.id,
  };
}

async function openWorkflow(
  definition: (typeof workflowDefinitions.value)[number],
  startNode: SQDCWorkflowDefinition["nodes"][number],
  firstTask = getSQDCWorkflowStartNodes(definition)[0],
) {
  const actionType = actionTypes.value.find(
    (candidate) => candidate.id === firstTask?.eventActionTypeId,
  );
  const shorthand = workflowShorthands.value.find(
    (candidate) => candidate.id === firstTask?.shorthandId,
  );
  const configuredActionType = shorthand?.EventActionType ?? actionType;
  if (configuredActionType == null) return;
  const modalActionType = getModalEventTypeForLatestRevision(
    configuredActionType as ModalEventType,
  );
  const defaultAction = createDefaultActionForWorkflow(
    props.event.Creator?.id ?? props.event.creatorId,
    shorthand,
    props.siteId,
  );
  selectedSource.value = {
    kind: "action-create",
    actionType: modalActionType,
    eventId: props.event.id,
    eventOwnerId: props.event.Creator?.id ?? props.event.creatorId,
    defaultAction,
    userOptionIds: getShorthandUserOptionIds(shorthand),
    workflow: { definitionId: definition.id, startNodeId: startNode.id },
    workflowDefinition: definition,
    workflowNode: firstTask,
    responsibleShorthand: shorthand,
  };
}

function isShorthandAvailableForBoard(shorthand: SQDCShorthand) {
  return shorthand.BoardConnections.some(
    (connection) => connection.Board?.id === boardIdForCreation.value,
  );
}

function openActionModal(actionType: ModalEventType, defaultAction?: ModalActionEditType) {
  selectedSource.value = {
    kind: "action-create",
    actionType,
    eventId: props.event.id,
    eventOwnerId: props.event.Creator?.id ?? props.event.creatorId,
    defaultAction,
  };
}

function getShorthandUserOptionIds(shorthand: SQDCShorthand | undefined) {
  return (
    shorthand?.UserOptions.map((option) => option.User?.id).filter(
      (id): id is number => id != null,
    ) ?? []
  );
}

function openSQDCShorthandModal(shorthand: SQDCShorthand) {
  if (shorthand.EventActionType == null) {
    return;
  }

  const modalActionType = getModalEventTypeForLatestRevision(
    shorthand.EventActionType as ModalEventType,
  );
  const defaultAction = createDefaultActionFromShorthand(
    shorthand,
    props.event.Creator?.id ?? props.event.creatorId,
    props.siteId,
  );
  selectedSource.value = {
    kind: "action-create",
    actionType: modalActionType,
    eventId: props.event.id,
    defaultAction,
    userOptionIds: getShorthandUserOptionIds(shorthand),
    responsibleShorthand: shorthand,
  };
}

function hideModal() {
  selectedSource.value = undefined;
}

const menuItems = computed<LISAMenuGroup[]>(() => {
  if (loadingMenu.value) {
    return [];
  }

  return [
    {
      label: t("components.event-action.event.action-buttons.EventActionButtonList.createAction"),
      items: actionMenuItems.value,
    },
    {
      label: t(
        "components.event-action.event.action-buttons.EventActionButtonList.createShorthand",
      ),
      items: shorthandButtonList.value,
    },
    {
      label: t("components.event-action.event.action-buttons.EventActionButtonList.createWorkflow"),
      items: workflowButtonList.value,
    },
    {
      label: t("components.event-action.event.action-buttons.EventActionButtonList.convertEvent"),
      items: conversionButtonList.value,
    },
  ].filter((section) => section.items.length > 0);
});
</script>
